import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

// --- Replicating External Helpers & Constants ---
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isNumericHeader = (header) => {
    // Check for and explicitly exclude descriptive keys (BRAND is included here)
    if (header.toUpperCase().includes('ACCOUNT') || header.toUpperCase().includes('CUSTOMER') || header.toUpperCase().includes('SALESPERSON') || header.toUpperCase().includes('GROUP') || header.toUpperCase().includes('BRAND')) {
        return false;
    }
    // Check if it contains common numeric keywords
    return ['SALES', 'RETURN', 'VAT', 'COMMISSION', 'NET', '%', 'AMT'].some(s => header.toUpperCase().includes(s));
};

const FOOTER_TOTAL_KEYS = [
    'NETCASH SALES', 
    'NETCREDIT SALES', 
    'NET SALERETURN', 
    'VAT AMT', 
    'NET SALES EXCLVAT'
];
// ------------------------------------------------

// Create styles for the PDF (Removing default right borders for explicit control)
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 10,
        fontSize: 8,
    },
    table: {
        display: 'flex',
        width: 'auto',
        flexDirection: 'column',
        // borderWidth: 0, 
    },
    // Styles for rows (handling top/bottom borders)
    headerRow: {
        flexDirection: 'row',
        backgroundColor: '#e0f7fa',
        // borderBottomWidth: 1,
        // borderBottomColor: '#ccc',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    row: {
        flexDirection: 'row',
        // borderBottomWidth: 1,
        // borderBottomColor: '#eee',
    },
    // Base cell styles (no default right border)
    cell: {
        padding: 4,
    },
    headerCell: {
        padding: 4,
        fontWeight: 'bold',
    },
    numeric: {
        textAlign: 'right',
    },
    left: {
        textAlign: 'left',
    },
    footerRow: {
        flexDirection: 'row',
        backgroundColor: '#80cbc4',
        // borderTopWidth: 2, 
        // borderTopColor: '#000',
        fontWeight: 'bold',
    },
    footerCell: {
        padding: 6,
        fontWeight: 'bold',
        fontSize: 10,
    },
    descriptiveCell: {
        minWidth: '20%',
        maxWidth: '20%', 
        flexGrow: 1,
    }
});

const SalesTableBrandAggregatedPDF = ({ rows }) => {
    // *** KEY CHANGE: Descriptive column is now BRAND ***
    const descriptiveColumnKey = 'BRAND';

    const allKeys = rows.length > 0 ? Object.keys(rows[0]) : [];
    const displayKeys = allKeys.filter(key =>
        !['isGroupHeader', 'isSubHeader', 'isSubSubHeader', 'totalAmount', 'totalAmountExclVAT', descriptiveColumnKey].includes(key)
    );

    if (rows.length === 0) {
        return (
            <Document>
                <Page size="A4" style={styles.page} orientation="landscape">
                    <Text style={{ textAlign: 'center', marginTop: 50 }}>No sales data found for the selected filters.</Text>
                </Page>
            </Document>
        );
    }

    // 2. Calculate the Grand Totals
    const internalGrandTotals = (() => {
        const keysToTotal = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
        const totals = {};
        for (const key of keysToTotal) {
            totals[key] = rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
        }
        return totals;
    })();
    
    const totalValueKeys = displayKeys.filter(key => FOOTER_TOTAL_KEYS.includes(key));
    const descriptiveHeader = descriptiveColumnKey.replace(/([A-Z])/g, ' $1').trim().toUpperCase();

    // 3. Determine column widths and footer span for layout
    const totalDisplayColumns = displayKeys.length;
    const descriptiveColumnWidth = 20; // 20%
    // Calculate the width for the rest of the columns
    const dynamicColumnWidth = ((100 - descriptiveColumnWidth) / totalDisplayColumns).toFixed(2); 

    let descriptiveSpanIndex = displayKeys.length;
    if (totalValueKeys.length > 0) {
        const firstTotalKey = totalValueKeys[0];
        descriptiveSpanIndex = displayKeys.indexOf(firstTotalKey);
    }
    
    // Width of the GRAND TOTAL label cell (20% + width of spanned columns)
    const descriptiveFooterWidth = (descriptiveColumnWidth * 1 + dynamicColumnWidth * descriptiveSpanIndex * 1).toFixed(2);
    const remainingFooterWidth = dynamicColumnWidth;


    return (
        <Document>
            <Page size="A4" style={styles.page} orientation="landscape">
                <View style={styles.table}>
                    
                    {/* Table Head */}
                    <View style={styles.headerRow} fixed>
                        {/* 1. Descriptive Column Header */}
                        <View style={[styles.headerCell, styles.descriptiveCell, { 
                            width: `${descriptiveColumnWidth}%`, 
                            // borderRightWidth: 1, 
                            // borderRightColor: '#ccc' 
                        }]}>
                            <Text>{descriptiveHeader}</Text>
                        </View>

                        {/* 2. Dynamic Headers (Explicit border management) */}
                        {displayKeys.map((key, dataIndex) => {
                             const lastCell = dataIndex === totalDisplayColumns - 1;
                             return (
                                <View 
                                    key={key} 
                                    style={[
                                        styles.headerCell, 
                                        isNumericHeader(key) ? styles.numeric : styles.left, 
                                        { 
                                            width: `${dynamicColumnWidth}%`,
                                            // borderRightWidth: lastCell ? 0 : 1, // Explicitly 0 or 1
                                            // borderRightColor: '#ccc',
                                        }
                                    ]}
                                >
                                    <Text>{key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}</Text>
                                </View>
                            );
                        })}
                    </View>

                    {/* Table Body */}
                    {rows.map((row, index) => (
                        <View key={`agg-acc-row-${index}`} style={styles.row} wrap={false}>
                            {/* 1. Descriptive Column Cell */}
                            <View style={[styles.cell, styles.left, styles.descriptiveCell, { 
                                width: `${descriptiveColumnWidth}%`, 
                                // borderRightWidth: 1, 
                                // borderRightColor: '#eee' 
                            }]}>
                                <Text>{row[descriptiveColumnKey]}</Text>
                            </View>

                            {/* 2. Data Cells (Explicit border management) */}
                            {displayKeys.map((key, dataIndex) => {
                                const isNumeric = isNumericHeader(key);
                                const lastCell = dataIndex === totalDisplayColumns - 1;

                                return (
                                    <View 
                                        key={key} 
                                        style={[
                                            styles.cell, 
                                            isNumeric ? styles.numeric : styles.left, 
                                            { 
                                                width: `${dynamicColumnWidth}%`,
                                                // borderRightWidth: lastCell ? 0 : 1, // Explicitly 0 or 1
                                                // borderRightColor: '#eee',
                                            }
                                        ]}
                                    >
                                        <Text>{isNumeric ? formatNumber(row[key]) : row[key]}</Text>
                                    </View>
                                );
                            })}
                        </View>
                    ))}

                    {/* Fixed Grand Totals Footer */}
                    {totalValueKeys.length > 0 && (
                        <View style={styles.footerRow} fixed>
                            {/* 1. Descriptive Span for the GRAND TOTAL label */}
                            <View 
                                style={[
                                    styles.footerCell, 
                                    styles.numeric, 
                                    { 
                                        width: `${descriptiveFooterWidth}%`, 
                                        // borderRightWidth: 0 // Explicitly 0
                                    }
                                ]}
                            >
                                <Text>GRAND TOTAL:</Text>
                            </View>
                            
                            {/* 2. Data cells where the totals begin (Explicit border management) */}
                            {displayKeys.slice(descriptiveSpanIndex).map((key, index) => {
                                const isTotalKey = FOOTER_TOTAL_KEYS.includes(key);
                                const lastCell = index === displayKeys.slice(descriptiveSpanIndex).length - 1;
                                
                                return (
                                    <View 
                                        key={key} 
                                        style={[
                                            styles.footerCell, 
                                            styles.numeric, 
                                            { 
                                                width: `${remainingFooterWidth}%`, 
                                                // borderRightWidth: lastCell ? 0 : 1, // Explicitly 0 or 1
                                                // borderRightColor: '#000',
                                            }
                                        ]}
                                    >
                                        <Text>{isTotalKey ? formatNumber(internalGrandTotals[key]) : ''}</Text>
                                    </View>
                                );
                            })}
                        </View>
                    )}
                </View>
            </Page>
        </Document>
    );
};

export default SalesTableBrandAggregatedPDF;